// @section 1 MobileFeed — shared primitives for the phone "feed" pattern.
// Atlas board/list screens (Pipeline, Acquisitions, Clients, Workplan) fork to
// a single heat/urgency-sorted column on phones instead of a swipe-sideways
// kanban or a wide table. These pieces keep that view consistent across screens:
//   FeedScroll  — the self-scrolling root (the canvas is overflow:hidden)
//   FeedHeader  — minimal title + one summary line (no breadcrumb / KPI rail)
//   FeedPills   — the filter/segment row
//   FeedCard    — one item: title + value, a chip, a status dot, optional meta
// Styled with the v5 CSS vars so it matches the dark Atlas canvas.

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

// @section 2 Layout primitives

// @block 2.1 Self-scrolling root with phone gutters.
export function FeedScroll({ children }: { children: ReactNode }) {
  return (
    <div style={{
      height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '14px 14px 24px', color: 'var(--tx-1)',
    }}>
      {children}
    </div>
  );
}

// @block 2.2 Minimal header — title + a single dense summary line.
export function FeedHeader({ title, summary }: { title: string; summary: ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--tx-0)', fontFamily: 'var(--t-display)' }}>{title}</h1>
      <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 4 }}>{summary}</div>
    </div>
  );
}

// @block 2.3 Filter / segment pill row.
export type FeedPillOption<T extends string> = { key: T; label: string; ct?: number };
export function FeedPills<T extends string>({
  options, value, onChange,
}: {
  options: FeedPillOption<T>[];
  value: T;
  onChange: (key: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button key={o.key} onClick={() => onChange(o.key)} style={{
            fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 99, cursor: 'pointer',
            border: active ? 'none' : '1px solid var(--t2-border)',
            background: active ? 'var(--brand-3)' : 'rgba(255,255,255,0.04)',
            color: active ? '#062028' : 'var(--tx-2)', fontFamily: 'inherit',
          }}>
            {o.label}{o.ct != null ? <span style={{ opacity: 0.7 }}> {o.ct}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

// @block 2.4 Empty-state line.
export function FeedEmpty({ children }: { children: ReactNode }) {
  return <div style={{ color: 'var(--tx-4)', fontStyle: 'italic', padding: '24px 4px' }}>{children}</div>;
}

// @section 3 FeedCard — one row in the feed.
// Renders as a NavLink when `to` is set, else a button with `onClick`. The
// status dot color encodes heat/urgency (caller picks the ramp color).
export type FeedStatus = { label: string; color: string };
type FeedCardProps = {
  title: string;
  value?: string;
  chip?: string;
  status?: FeedStatus;
  trailing?: string;
  meta?: string;
  to?: string;
  onClick?: () => void;
};

export function FeedCard({ title, value, chip, status, trailing, meta, to, onClick }: FeedCardProps) {
  const inner = (
    <>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: 'var(--tx-0)', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
          {value && <span style={{ color: 'var(--brand-4)', fontWeight: 600, fontSize: 13, flexShrink: 0 }}>{value}</span>}
        </div>
        {(chip || status || trailing) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {chip && <span style={{ fontSize: 11, color: 'var(--brand-3)', background: 'rgba(34,211,238,0.12)', padding: '2px 8px', borderRadius: 99 }}>{chip}</span>}
            {status && (
              <span style={{ fontSize: 11, color: status.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: status.color, display: 'inline-block' }} />
                {status.label}
              </span>
            )}
            {trailing && <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--tx-4)', fontVariantNumeric: 'tabular-nums' }}>{trailing}</span>}
          </div>
        )}
        {meta && <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta}</div>}
      </div>
      <span style={{ color: 'var(--tx-4)', fontSize: 18, flexShrink: 0 }} aria-hidden="true">›</span>
    </>
  );

  const style: React.CSSProperties = {
    display: 'flex', gap: 10, alignItems: 'center', textAlign: 'left', width: '100%',
    background: 'rgba(255,255,255,0.03)', border: '1px solid var(--t2-border)',
    borderRadius: 10, padding: '11px 12px', cursor: 'pointer', fontFamily: 'inherit',
    textDecoration: 'none', marginBottom: 8,
  };

  if (to) return <NavLink to={to} style={style}>{inner}</NavLink>;
  return <button onClick={onClick} style={style}>{inner}</button>;
}
